import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
// material
import { Stack, Box, Typography, Button, TextField } from '@mui/material';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// material
import { LoadingButton } from '@mui/lab';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// components
import UploadFileComponent from './upload';
// services
import { updateEvent } from '../../../services/Event';

EventFormTheme.propTypes = {
  event: PropTypes.object,
  handleBack: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

export default function EventFormTheme({ event, handleBack, edit }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [colorSuccess, setColorSuccess] = useState(false);

  const eventThemeValues = {
    theme: {
      color: (event && event['theme.color']) || event?.theme?.color || ''
    }
  };

  const EventThemeSchema = Yup.object().shape({
    theme: Yup.object({
      color: Yup.string()
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: eventThemeValues,
    validationSchema: EventThemeSchema,
    onSubmit: async (values) => {
      setColorSuccess(false);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const payload = {
          'theme.color': values.theme.color
        };

        await updateEvent({ headers, payload, id: event._id });
        setEvent((event) => ({ ...event, ...payload }));
        setColorSuccess(true);
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const { touched, errors, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ maxWidth: '300px' }}>
            <Typography component="h2" variant="h6">
              Primary Color
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Color"
                placeholder="#000000"
                {...getFieldProps('theme.color')}
                error={Boolean(touched.theme?.color && errors.theme?.color)}
                helperText={touched.theme?.color && errors.theme?.color}
              />

              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ ml: 1 }}
                endIcon={colorSuccess && <Icon icon={checkmarkCircleFill} />}
              >
                Save
              </LoadingButton>
            </Box>

            {errors.theme?.color && (
              <Typography variant="subtitle2" component="span" color="error">
                {errors.theme.color}
              </Typography>
            )}
          </Stack>
        </Form>
      </FormikProvider>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Typography component="h2" variant="h6">
          Upload Logo
          <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
            This logo will appear on a white background
          </Typography>
          <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
            Best size 500x160
          </Typography>
        </Typography>

        <UploadFileComponent
          event={event}
          fileUrl={(event && event['theme.logo.regular.url']) || event?.theme?.logo?.regular?.url}
          fileField="theme.logo.regular.url"
        />

        <Typography component="h2" variant="h6">
          Upload Logo for Dark Background
          <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
            This logo will appear over the cover banner
          </Typography>
          <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
            Best size 500x160
          </Typography>
        </Typography>

        <UploadFileComponent
          event={event}
          fileUrl={(event && event['theme.logo.inverse.url']) || event?.theme?.logo?.inverse?.url}
          fileField="theme.logo.inverse.url"
          background="grey"
        />

        <Typography component="h2" variant="h6">
          Cover Banner
          <Typography component="span" variant="body2" color="grey.500" sx={{ display: 'block' }}>
            Best size 1680x470
          </Typography>
        </Typography>

        <UploadFileComponent
          event={event}
          fileUrl={(event && event['theme.cover.url']) || event?.theme?.cover?.url}
          fileField="theme.cover.url"
        />

        {!edit && (
          <Box>
            <Button size="large" onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
